/**
 * The cradle pair's load-time check in the live game (D-TBD; the pure check is `fleet-link/pairCheck.ts`). On every
 * worker re-init from a save (`strategic-link onWorkerReinit` with `fromSave`: Continue, a slot load, an import; a
 * fresh Reach has no pair to check) one check is queued; the next frame reads the worker fleet view, drops a
 * Career-only half through the career's write hook, returns a worker-only `CARRIED_UNTIL` ship to the pool docked
 * at the pilot's station, and logs one warning after the sends, with their outcomes. When the flown ships disagree
 * (a save cut mid switch) nothing is repaired and that one warning says so.
 *
 * A Career write refused `notReady` (the save still loading) keeps the check queued, nothing sent or logged, for at
 * most `maxRetries` more checks (then one warning); any other refusal logs one warning and is not retried, so a
 * persistent refusal never rebuilds the fleet view every frame.
 *
 * Event-rate (a load), so it may allocate. A consistent pair writes, sends and logs nothing.
 */
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import { careerFlownId, checkCarrierPair, pairAgrees, pairCheckWarning, type PairCheck, type PairCheckShip, type ReleaseOutcome } from '../../fleet-link/pairCheck'
import type { CarrierCommand } from '../../strategic/schemas'

export interface PairCheckDeps {
  /** The worker fleet view (its `carried`/`flown` flags and stations). */
  fleetView(): Promise<{ readonly ships: readonly PairCheckShip[] }>
  /** Sends a cradle-pair command to the worker (`StrategicClient.command`). */
  command(cmd: CarrierCommand): Promise<{ ok: true } | { ok: false; error: string }>
  career(): Career
  /** The career's write hook (`applyDevCommand`); `notReady` while a save loads. */
  write(name: string, cmd: (c: Career) => Career): { ok: true } | { ok: false; code: string }
}

/**
 * What one check did. `retry`: the Career write was refused `notReady` (nothing sent or logged); `refused`: refused
 * otherwise (one warning); `mismatch`: the flown ships disagree (one warning, nothing repaired).
 */
export type PairCheckRun = { readonly status: 'agree' | 'repaired' | 'mismatch' | 'retry' | 'refused'; readonly check: PairCheck }

/** One check (see the file header). */
export async function runPairCheck(deps: PairCheckDeps): Promise<PairCheckRun> {
  const view = await deps.fleetView()
  const check = checkCarrierPair(deps.career(), view.ships)
  if (pairAgrees(check)) return { status: 'agree', check }
  if (check.flownMismatch !== null) {
    logger.warn(pairCheckWarning(check, []) ?? 'parkedL: the loaded cradle pair was not checked (the flown ships disagree)')
    return { status: 'mismatch', check }
  }
  if (check.dropped.length > 0) {
    // Re-run on the Career the write sees (the same view), so a write in between is not undone.
    const w = deps.write('parkedL pair check', (c) => checkCarrierPair(c, view.ships).career)
    if (!w.ok) {
      if (w.code === 'notReady') return { status: 'retry', check }
      logger.warn(`parkedL: the pair check's Career write refused (${w.code}); nothing repaired`)
      return { status: 'refused', check }
    }
  }
  const career = deps.career()
  const carrierId = careerFlownId(career)
  const released: ReleaseOutcome[] = []
  for (const shipId of check.release) {
    const r = await deps.command({ type: 'launchFromCarrier', carrierId, shipId, stationId: career.lastDockedStation })
    released.push({ id: shipId, error: r.ok ? null : r.error })
  }
  const warning = pairCheckWarning(check, released)
  if (warning !== null) logger.warn(warning)
  return { status: 'repaired', check }
}

export interface PairCheckQueue {
  /** Queues one check (a worker re-init from a save); resets the retry budget. */
  request(): void
  /** Per frame: starts a queued check when none runs (a boolean test while idle). */
  frame(): void
  /** The running check, or null (tests). */
  readonly running: Promise<PairCheckRun> | null
}

export function createPairCheckQueue(deps: PairCheckDeps, maxRetries: number): PairCheckQueue {
  let pending = false
  let retriesLeft = maxRetries
  let running: Promise<PairCheckRun> | null = null
  return {
    request(): void {
      pending = true
      retriesLeft = maxRetries
    },
    frame(): void {
      if (!pending || running !== null) return
      pending = false
      running = runPairCheck(deps).then(
        (r) => {
          running = null
          if (r.status === 'retry') {
            if (retriesLeft > 0) {
              retriesLeft--
              pending = true
            } else logger.warn('parkedL: the pair check gave up (the Career stayed not ready)')
          }
          return r
        },
        (err: unknown) => {
          running = null
          logger.warn('parkedL: the pair check failed', err)
          return { status: 'agree', check: checkCarrierPair(deps.career(), []) } satisfies PairCheckRun
        },
      )
    },
    get running(): Promise<PairCheckRun> | null {
      return running
    },
  }
}

/** The strategic boot's re-init hook (`StrategicBoot.onWorkerReinit`), narrowed to what the check reads. */
export interface ReinitSource {
  onWorkerReinit(fn: (e: { readonly fromSave: boolean }) => void): () => void
}

export interface PairCheckHook {
  readonly queue: PairCheckQueue
  /** Per frame (the feature's `frame`). */
  frame(): void
  dispose(): void
}

/**
 * The feature's wiring: loads the boot (`boot`, a dynamic import in the game), subscribes to its re-init unless
 * disposed first, queues a check on each re-init from a save, and runs it from `frame`.
 */
export function createPairCheckHook(deps: PairCheckDeps, maxRetries: number, boot: () => Promise<ReinitSource | null>): PairCheckHook {
  const queue = createPairCheckQueue(deps, maxRetries)
  let disposed = false
  let off: (() => void) | null = null
  void boot().then(
    (b) => {
      if (disposed || b === null) return
      off = b.onWorkerReinit((e) => {
        if (e.fromSave) queue.request()
      })
    },
    (err: unknown) => logger.warn('parkedL: no worker re-init hook for the pair check', err),
  )
  return {
    queue,
    frame(): void {
      if (!disposed) queue.frame()
    },
    dispose(): void {
      disposed = true
      off?.()
      off = null
    },
  }
}
